import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import PageHeader from "@/components/PageHeader";
import { QueryTable } from "@/components/query-table/QueryTable";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Module } from "@/constants";
import { toCamelCase } from "@/lib/utils";
import { EmailRecipientsAddUsersDialog } from "@/modules/email-recipients/components/EmailRecipientsAddUsersDialog";
import { EmailRecipientsListContent } from "@/modules/email-recipients/components/EmailRecipientsListContent";
import { EmailRecipientsToolbar } from "@/modules/email-recipients/components/EmailRecipientsToolbar";
import { EmailRecipientsToolbarActions } from "@/modules/email-recipients/components/EmailRecipientsToolbarActions";
import { useEmailRecipientBreadcrumbs } from "@/modules/email-recipients/hooks/useEmailRecipientBreadcrumbs";
import { useEmailRecipientsDirectoryColumnOverrides } from "@/modules/email-recipients/hooks/useEmailRecipientsDirectoryColumnOverrides";
import { useEmailRecipientsQuery } from "@/modules/email-recipients/hooks/useEmailRecipientsQuery";
import { displayEmailRecipient } from "@/schemas/emailRecipient";
import { DisplayEmailRecipient } from "@/types/emailRecipient";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const EmailRecipientsDirectory = () => {
  const { t } = useTranslation();
  const emailRecipientsColumnOverrides =
    useEmailRecipientsDirectoryColumnOverrides();
  const [emailTypeId, setEmailTypeId] = useState<string | undefined>(undefined);
  const [isAddUsersDialogOpened, setIsAddUsersDialogOpened] = useState(false);

  const name = toCamelCase(Module.EmailRecipients);
  const breadcrumbs = useEmailRecipientBreadcrumbs();

  const handleTypeChange = useCallback((nextTypeId: string | undefined) => {
    setEmailTypeId(nextTypeId);
  }, []);

  const {
    query: emailRecipientsQuery,
    options,
    queryKey,
  } = useEmailRecipientsQuery({
    emailTypeId,
  });

  const tableFeatures = useMemo(
    () => ({
      enableRowSelection: true,
      enableSelectAll: true,
      enableFilters: true,
      enableSorting: true,
    }),
    [],
  );

  const emailRecipientRowId = useCallback(
    (row: DisplayEmailRecipient) => row.recipient_id,
    [],
  );

  return (
    <>
      <PageHeader
        title={t("emailRecipients.title.multiple")}
        breadcrumbItems={breadcrumbs}
      />
      <RowSelectionProvider>
        <QueryTable
          schema={displayEmailRecipient}
          name={name}
          query={emailRecipientsQuery}
          options={options}
          tableFeatures={tableFeatures}
          translationKey="emailRecipients"
          getRowId={emailRecipientRowId}
          columnOverrides={emailRecipientsColumnOverrides}
        >
          {!emailTypeId ? (
            <>
              <EmailRecipientsToolbar
                emailTypeId={emailTypeId}
                onTypeChange={handleTypeChange}
                actions={
                  <EmailRecipientsToolbarActions
                    emailTypeId={emailTypeId}
                    isRemoveSubmitting={false}
                    isDisabled
                  />
                }
              />
              <Alert>
                <AlertTitle>
                  {t("emailRecipients.selectType.placeholder")}
                </AlertTitle>
              </Alert>
            </>
          ) : (
            <EmailRecipientsListContent
              emailTypeId={emailTypeId}
              queryKey={queryKey}
              onTypeChange={handleTypeChange}
              emailRecipientsQuery={emailRecipientsQuery}
              onOpenAddUsers={() => setIsAddUsersDialogOpened(true)}
            />
          )}
        </QueryTable>
        {emailTypeId && (
          <EmailRecipientsAddUsersDialog
            emailTypeId={emailTypeId}
            queryKey={queryKey}
            isOpen={isAddUsersDialogOpened}
            onOpenChange={setIsAddUsersDialogOpened}
          />
        )}
      </RowSelectionProvider>
    </>
  );
};
